import React, { useMemo, useState } from "react";
import {
  Chart,
  Title,
  XAxis,
  YAxis,
  Series,
  Tooltip,
  Legend,
} from "@highcharts/react";
import "../../css/calculators/MortgageBorrow.css";
import SecondaryButton from "../designSystem/buttons/TertiaryButton";
import PrimaryButton from "../designSystem/buttons/PrimaryButton";
import FormattedNumberInput from "./HelperFunctions/FormattedNumberInput";

const initialState = {
  desiredAnnualIncome: "",
  currentPensionPot: "",
  additionalMonthlyPayments: "",
  currentAge: "",
  retirementAge: "",
  annualReturn: "5.0",
  inflation: "2.5",
  managementFees: "1.04",
};

const WITHDRAWAL_RATE = 0.04;

const cleanNumericInput = (value) => value.replace(/[^\d.]/g, "");

const formatCurrency = (value, decimals = 0) => {
  return Number(value || 0).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const formatNumber = (value, decimals = 2) => {
  return Number(value || 0).toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

const buildPensionProjection = ({
  currentPensionPot,
  additionalMonthlyPayments,
  currentAge,
  retirementAge,
  annualReturn,
  inflation,
  managementFees,
}) => {
  const startingPot = Number(currentPensionPot || 0);
  const monthlyContribution = Number(additionalMonthlyPayments || 0);
  const startAge = Number(currentAge || 0);
  const endAge = Number(retirementAge || 0);
  const grossReturn = Number(annualReturn || 0) / 100;
  const inflationRate = Number(inflation || 0) / 100;
  const feeRate = Number(managementFees || 0) / 100;

  const yearsToRetirement = Math.max(endAge - startAge, 0);
  const netAnnualReturn = grossReturn - feeRate;
  const monthlyRate = netAnnualReturn / 12;

  let currentFund = startingPot;
  const yearlyProjection = [];

  yearlyProjection.push({
    age: startAge,
    pensionPot: Number(currentFund.toFixed(2)),
    pensionPotTodayValue: Number(currentFund.toFixed(2)),
  });

  for (let year = 1; year <= yearsToRetirement; year += 1) {
    for (let month = 0; month < 12; month += 1) {
      currentFund += monthlyContribution;

      if (monthlyRate !== 0) {
        currentFund *= 1 + monthlyRate;
      }
    }

    const age = startAge + year;
    const todaysValue = currentFund / Math.pow(1 + inflationRate, year);

    yearlyProjection.push({
      age,
      pensionPot: Number(currentFund.toFixed(2)),
      pensionPotTodayValue: Number(todaysValue.toFixed(2)),
    });
  }

  const projectedFund =
    yearlyProjection[yearlyProjection.length - 1]?.pensionPot || 0;
  const todaysMoney =
    yearlyProjection[yearlyProjection.length - 1]?.pensionPotTodayValue || 0;

  return {
    yearsToRetirement,
    yearlyProjection,
    projectedFund,
    todaysMoney,
    netAnnualReturn,
  };
};

export default function PensionShortfallCalculator() {
  const [formData, setFormData] = useState(initialState);
  const [hasCalculated, setHasCalculated] = useState(false);

 const handleChange = (e) => {
   const { name, value } = e.target;

   setFormData((prev) => ({
     ...prev,
     [name]: cleanNumericInput(value),
   }));
 };

  const handleCalculate = (e) => {
    e.preventDefault();
    setHasCalculated(true);
  };

  const handleClear = () => {
    setFormData(initialState);
    setHasCalculated(false);
  };

  const results = useMemo(() => {
    if (!hasCalculated) return null;

    const desiredAnnualIncome = Number(formData.desiredAnnualIncome || 0);

    const projection = buildPensionProjection({
      currentPensionPot: formData.currentPensionPot,
      additionalMonthlyPayments: formData.additionalMonthlyPayments,
      currentAge: formData.currentAge,
      retirementAge: formData.retirementAge,
      annualReturn: formData.annualReturn,
      inflation: formData.inflation,
      managementFees: formData.managementFees,
    });

    const requiredFund = desiredAnnualIncome / WITHDRAWAL_RATE;
    const fundShortfall = Math.max(requiredFund - projection.projectedFund, 0);

    const projectedAnnualPension = projection.projectedFund * WITHDRAWAL_RATE;
    const projectedMonthlyPension = projectedAnnualPension / 12;

    const projectedAnnualPensionToday =
      projection.todaysMoney * WITHDRAWAL_RATE;
    const projectedMonthlyPensionToday = projectedAnnualPensionToday / 12;

    const annualIncomeShortfall = Math.max(
      desiredAnnualIncome - projectedAnnualPension,
      0,
    );

    const monthlyIncomeShortfall = annualIncomeShortfall / 12;

    const totalMonths = projection.yearsToRetirement * 12;
    const monthlyRate = projection.netAnnualReturn / 12;

    let additionalMonthlyPremium = 0;

    if (fundShortfall > 0 && totalMonths > 0) {
      if (monthlyRate === 0) {
        additionalMonthlyPremium = fundShortfall / totalMonths;
      } else {
        additionalMonthlyPremium =
          fundShortfall /
          ((Math.pow(1 + monthlyRate, totalMonths) - 1) / monthlyRate);
      }
    }

    const singlePremiumNeeded =
      totalMonths > 0
        ? fundShortfall / Math.pow(1 + monthlyRate, totalMonths)
        : fundShortfall;

    return {
      ...projection,
      desiredAnnualIncome,
      requiredFund,
      fundShortfall,
      projectedAnnualPension,
      projectedMonthlyPension,
      projectedAnnualPensionToday,
      projectedMonthlyPensionToday,
      annualIncomeShortfall,
      monthlyIncomeShortfall,
      additionalMonthlyPremium,
      singlePremiumNeeded,
    };
  }, [formData, hasCalculated]);

  const canCalculate =
    formData.desiredAnnualIncome &&
    formData.currentPensionPot &&
    formData.currentAge &&
    formData.retirementAge;

  const chartOptions = useMemo(() => {
    if (!results) return null;

    return {
      chart: {
        type: "line",
        backgroundColor: "#f3f3f3",
        borderRadius: 18,
        height: 360,
        spacingTop: 30,
        spacingRight: 20,
        spacingBottom: 20,
        spacingLeft: 20,
      },
      title: {
        text: "Projected value of pension pot",
        align: "left",
        style: {
          color: "#033239",
          fontSize: "20px",
          fontWeight: "700",
        },
      },
      credits: {
        enabled: false,
      },
      xAxis: {
        categories: results.yearlyProjection.map((item) => item.age),
        title: {
          text: "",
        },
        lineColor: "#d9d9d9",
        tickColor: "#d9d9d9",
        labels: {
          style: {
            color: "#2d2d2d",
            fontSize: "14px",
          },
        },
      },
      yAxis: {
        title: {
          text: "",
        },
        gridLineColor: "#e3e3e3",
        labels: {
          formatter: function () {
            return formatCurrency(this.value, 2);
          },
          style: {
            color: "#2d2d2d",
            fontSize: "14px",
          },
        },
      },
      tooltip: {
        shared: true,
        formatter: function () {
          const points = this.points || [];
          let html = `<b>${this.x}</b><br/>`;

          points.forEach((point) => {
            html += `${point.series.name}: <b>${formatCurrency(point.y, 0)}</b><br/>`;
          });

          return html;
        },
      },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        itemStyle: {
          color: "#2d2d2d",
          fontSize: "16px",
          fontWeight: "400",
        },
      },
      plotOptions: {
        series: {
          lineWidth: 2,
          marker: {
            enabled: true,
            radius: 4,
            symbol: "circle",
          },
        },
      },
      series: [
        {
          name: "Pension Pot",
          data: results.yearlyProjection.map((item) =>
            Number(item.pensionPot.toFixed(2)),
          ),
          color: " #5EB5AA",
        },
        {
          name: "Pension Pot Today Value",
          data: results.yearlyProjection.map((item) =>
            Number(item.pensionPotTodayValue.toFixed(2)),
          ),
          color: "#111111",
        },
      ],
    };
  }, [results]);

  return (
    <div className="borrow-calculator-page">
      <div className="borrow-form-wrap">
        <form onSubmit={handleCalculate}>
          <div className="title primary">Pensions Shortfall Calculator</div>

          <div className="borrow-field-group">
            <label className="borrow-label">Desired annual income (£)</label>
            <div className="select-wrapper">
              <FormattedNumberInput
                name="desiredAnnualIncome"
                value={formData.desiredAnnualIncome}
                onChange={(raw) =>
                  setFormData((prev) => ({
                    ...prev,
                    desiredAnnualIncome: raw,
                  }))
                }
                className="borrow-input "
              />
            </div>
          </div>

          <div className="borrow-field-group">
            <label className="borrow-label">
              Current value of pension pots (£)
            </label>
            <div className="select-wrapper">
              <FormattedNumberInput
                name="currentPensionPot"
                value={formData.currentPensionPot}
                onChange={(raw) =>
                  setFormData((prev) => ({
                    ...prev,
                    currentPensionPot: raw,
                  }))
                }
                className="borrow-input "
              />
            </div>
          </div>

          <div className="borrow-field-group">
            <label className="borrow-label">
              Current value of additional monthly payments (£)
            </label>
            <div className="select-wrapper">
              <FormattedNumberInput
                name="additionalMonthlyPayments"
                value={formData.additionalMonthlyPayments}
                onChange={(raw) =>
                  setFormData((prev) => ({
                    ...prev,
                    additionalMonthlyPayments: raw,
                  }))
                }
                className="borrow-input "
              />
            </div>
          </div>

          <div className="borrow-field-group">
            <label className="borrow-label">Current age</label>
            <input
              type="text"
              name="currentAge"
              value={formData.currentAge}
              onChange={handleChange}
              className="borrow-input"
            />
          </div>

          <div className="borrow-field-group">
            <label className="borrow-label">Retirement age</label>
            <input
              type="text"
              name="retirementAge"
              value={formData.retirementAge}
              onChange={handleChange}
              className="borrow-input"
            />
          </div>

          <div className="borrow-field-group">
            <label className="borrow-label">Annual return assumption (%)</label>
            <input
              type="text"
              name="annualReturn"
              value={formData.annualReturn}
              onChange={handleChange}
              className="borrow-input"
            />
          </div>

          <div className="borrow-field-group">
            <label className="borrow-label">Inflation (%)</label>
            <input
              type="text"
              name="inflation"
              value={formData.inflation}
              onChange={handleChange}
              className="borrow-input"
            />
          </div>

          <div className="borrow-field-group">
            <label className="borrow-label">Management fees (%)</label>
            <input
              type="text"
              name="managementFees"
              value={formData.managementFees}
              onChange={handleChange}
              className="borrow-input"
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: 16,
              marginTop: 24,
              justifyContent: "flex-end",
            }}
          >
            <SecondaryButton onClick={handleClear} text="Clear" />
            <PrimaryButton onClick={handleCalculate} text="Calculate" />
          </div>
        </form>
      </div>

      {results && (
        <div className="borrow-result-wrap">
          <div className="result-container">
            <div className="title">Result</div>

            <div
              style={{
                background: "#f3f3f3",
                borderRadius: "18px",
                padding: "22px",
                marginBottom: "34px",
              }}
            >
              <Chart
                options={chartOptions}
                containerProps={{
                  style: { width: "100%", height: "360px" },
                }}
              >
                <Title>Projected value of pension pot</Title>
                <XAxis
                  categories={results.yearlyProjection.map((item) => item.age)}
                />
                <YAxis />
                <Series
                  type="line"
                  name="Pension Pot"
                  data={results.yearlyProjection.map((item) =>
                    Number(item.pensionPot.toFixed(2)),
                  )}
                />
                <Series
                  type="line"
                  name="Pension Pot Today Value"
                  data={results.yearlyProjection.map((item) =>
                    Number(item.pensionPotTodayValue.toFixed(2)),
                  )}
                />
                <Tooltip />
                <Legend />
              </Chart>
            </div>

            <div className="result-section">
              <div className="result-heading">Annual income goal</div>
              <div className="result-value">
                {formatCurrency(results.desiredAnnualIncome)}
              </div>
            </div>

            <div className="result-section">
              <div className="result-heading">Pension pot</div>
              <div className="result-value">
                {formatCurrency(results.projectedFund)}
              </div>
              <p className="result-copy">
                The estimated final value of your pension pot.
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">Today&apos;s money</div>
              <div className="result-value">
                {formatCurrency(results.todaysMoney)}
              </div>
              <p className="result-copy">
                This figure takes into account inflation which reduces your
                buying power over time. It provides an indication of the amount
                that would be needed today.
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">Projected Fund</div>
              <p className="result-copy">
                {formatCurrency(results.projectedFund)} (
                {formatCurrency(results.todaysMoney)} in today&apos;s terms)
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">Projected Pension</div>
              <p className="result-copy">
                The annual projected pension is{" "}
                {formatCurrency(results.projectedAnnualPension)} (
                {formatCurrency(results.projectedAnnualPensionToday)} in
                today&apos;s terms) per year, which means the monthly pension
                payment will be{" "}
                {formatCurrency(results.projectedMonthlyPension)} (
                {formatCurrency(results.projectedMonthlyPensionToday)} in
                today&apos;s terms) per month.
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">Fund Shortfall</div>
              <p className="result-copy">
                The requested annual pension is{" "}
                {formatCurrency(results.desiredAnnualIncome)}, resulting in a
                required pension fund of {formatCurrency(results.requiredFund)};
                however, the projected fund is{" "}
                {formatCurrency(results.projectedFund)}, which means there is a
                fund shortfall of {formatCurrency(results.fundShortfall)}.
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">
                Pension Monthly Payment Shortfall
              </div>
              <p className="result-copy">
                The required annual income is{" "}
                {formatCurrency(results.desiredAnnualIncome)}, which means that
                the monthly pension payment required is{" "}
                {formatCurrency(results.desiredAnnualIncome / 12)}; however, the
                projected monthly pension payment is{" "}
                {formatCurrency(results.projectedMonthlyPension)}, which means
                there is a monthly pension shortfall of{" "}
                {formatCurrency(results.monthlyIncomeShortfall)}.
              </p>
            </div>

            <div className="result-section">
              <div className="result-heading">Shortfall</div>
              <p className="result-copy">
                Given your desired retirement age and income, there is a funding
                shortfall of {formatCurrency(results.fundShortfall)} resulting
                in an annual pension income shortfall of{" "}
                {formatCurrency(results.annualIncomeShortfall)}.
              </p>
              <p className="result-copy" style={{ marginTop: "14px" }}>
                An additional monthly premium of{" "}
                {formatCurrency(results.additionalMonthlyPremium)} or a single
                premium of {formatCurrency(results.singlePremiumNeeded)} would
                offset the shortfall.
              </p>
            </div>

            <div className="result-divider" />

            <div className="result-note-title">Please note</div>
            <p className="result-note-copy">
              This pensions shortfall calculator is an interactive tool which
              should be used for your guidance only, and must not form part of
              your financial decision making process.
            </p>
            <p className="result-note-copy">
              This pension calculator is for illustrative purposes only and does
              not constitute investment advice or create any legal obligations.
              Users should form their own assessment, seek independent
              professional advice, and be aware that investments can fall as
              well as rise, with no guarantee of returns.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
